import './registerData.css';
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getStages, getClassesByStage, secondRegistration } from '../../redux/slices/authSlice';

function RegisterData() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { 
        userType, 
        phone, 
        isLoading, 
        error, 
        stages, 
        classes, 
        stagesLoading, 
        classesLoading,
        registrationComplete 
    } = useSelector((state) => state.auth);
    
    const [formData, setFormData] = useState({
        prof_img: null,
        email: '',
        name: '',
        password: '',
        confirmPassword: '',
        learn_type: 'courses',
        role: userType,
        stage_id: '',
        class_id: ''
    });
    const [selectedStage, setSelectedStage] = useState('');
    const [currentSlide, setCurrentSlide] = useState(0);

    const slides = [
        {
            title: "منصة علمني التعليمية",
            content: "أكاديميتنا العملية تهدف الى تزويد المتعلمين بالمهارات العملية والتطبيقية التي تؤهلهم لسوق العمل من خلال برامج تدريبية متطورة وتجربة تعليمية تفاعلية."
        },
        {
            title: "تعلم بطريقة ممتعة",
            content: "نقدم محتوى تعليمي تفاعلي وممتع يساعد الطلاب على فهم المفاهيم بسهولة وتطبيقها في الحياة العملية."
        },
        {
            title: "محتوى عالي الجودة",
            content: "محتوى تعليمي متميز ومحدث باستمرار لضمان حصول الطلاب على أفضل تجربة تعليمية ممكنة."
        }
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 4000);

        return () => clearInterval(interval);
    }, [slides.length]);

    // إضافة انيميشن للعناصر عند التحميل
    useEffect(() => {
        const elements = document.querySelectorAll('.login-card, .login-info-section');
        elements.forEach((el, index) => {
            el.style.animationDelay = `${index * 0.2}s`;
        });

        // إضافة انيميشن للعناصر الداخلية
        const innerElements = document.querySelectorAll('.login-card-title, .login-card-subtitle, .form-group, .login-btn, .login-card-footer, .login-back-link');
        innerElements.forEach((el, index) => {
            el.style.animationDelay = `${(index + 2) * 0.1}s`;
        });

        // إضافة انيميشن للعناصر المعلوماتية
        const infoElements = document.querySelectorAll('.login-brand, .login-info-slider, .login-indicators');
        infoElements.forEach((el, index) => {
            el.style.animationDelay = `${(index + 1) * 0.2}s`;
        });
    }, []);

    // Redirect if no phone number or OTP not verified
    useEffect(() => {
        if (!phone) {
            navigate('/registerPhone');
        }
    }, [phone, navigate]);

    // Redirect if registration complete
    useEffect(() => {
        if (registrationComplete) {
            toast.success('تم إنشاء الحساب بنجاح! 🎉');
            navigate('/login');
        }
    }, [registrationComplete, navigate]);

    // Show error toast
    useEffect(() => {
        if (error) {
            toast.error(error);
        }
    }, [error]);

    // Load stages on component mount (only for students)
    useEffect(() => {
        if (userType === 'student') {
            dispatch(getStages());
        }
    }, [dispatch, userType]);

    // Load classes when stage changes (only for students)
    useEffect(() => {
        if (userType === 'student' && selectedStage) {
            dispatch(getClassesByStage(selectedStage));
        }
    }, [selectedStage, dispatch, userType]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        setFormData(prev => ({
            ...prev,
            prof_img: e.target.files[0]
        }));
    };

    const handleStageChange = (e) => {
        const stageId = e.target.value;
        setSelectedStage(stageId);
        setFormData(prev => ({
            ...prev,
            stage_id: stageId,
            class_id: '' // Reset class when stage changes
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Local validation for password confirmation
        if (formData.password !== formData.confirmPassword) {
            toast.error('كلمة المرور وتأكيدها غير متطابقين');
            return;
        }

        if (formData.password.length < 6) {
            toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
            return;
        }

        // Additional validation for students
        if (userType === 'student') {
            if (!formData.stage_id) {
                toast.error('يرجى اختيار المرحلة الدراسية');
                return;
            }
            if (!formData.class_id) {
                toast.error('يرجى اختيار الصف الدراسي');
                return;
            }
        }

        // Remove confirmPassword from data sent to API
        const { confirmPassword, ...submitData } = formData;
        
        // Remove stage_id and class_id for non-students (teachers and parents)
        if (userType !== 'student') {
            delete submitData.stage_id;
            delete submitData.class_id;
        }
        
        const finalData = {
            ...submitData,
            phone: phone
        };

        toast.info('جاري إنشاء الحساب...');
        dispatch(secondRegistration(finalData));
    };

    const getUserTypeText = () => {
        switch (userType) {
            case 'student': return 'طالب';
            case 'teacher': return 'معلم';
            case 'parent': return 'ولي أمر';
            default: return '';
        }
    };

    const getRoleValue = () => {
        switch (userType) {
            case 'student': return 'student';
            case 'teacher': return 'teacher';
            case 'parent': return 'parent';
            default: return 'student';
        }
    };

    return (
        <div className="login-page register-data-page">
            <div className="login-overlay">
                <div className="login-wrapper" style={{margin : "20px 0"}}>
                    {/* Right Info */}
                    <div className="login-info-section">
                        <h1 className="login-brand">علمني <i className="login-icon-book"></i></h1>

                        <div className="login-info-slider">
                            {slides.map((slide, index) => (
                                <div
                                    key={index}
                                    className={`login-slide ${index === currentSlide ? 'active' : ''}`}
                                >
                                    <h3 className="login-info-title">{slide.title}</h3>
                                    <p className="login-info-paragraph">{slide.content}</p>
                                </div>
                            ))}
                        </div>

                        {/* Indicators */}
                        <div className="login-indicators">
                            {slides.map((_, index) => (
                                <span
                                    key={index}
                                    className={index === currentSlide ? 'active' : ''}
                                    onClick={() => setCurrentSlide(index)}
                                    style={{ cursor: 'pointer' }}
                                ></span>
                            ))}
                        </div>
                    </div>

                    {/* Left Form */}
                    <div className="login-card register-data-card">
                        <h2 className="login-card-title">إكمال البيانات</h2>
                        <p className="login-card-subtitle">
                            أكمل بياناتك الشخصية لإنشاء حسابك
                        </p>

                        {/* User Type Display */}
                        <div className="user-type-display">
                            <span className="user-type-label">نوع الحساب:</span>
                            <span className="user-type-value">{getUserTypeText()}</span>
                        </div>

                        <form onSubmit={handleSubmit} className="registration-form">
                            {/* Profile Image */}
                            <div className="form-group">
                                <label htmlFor="prof_img">الصورة الشخصية</label>
                        <input
                                    type="file"
                                    id="prof_img"
                                    name="prof_img"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    className="form-input"
                                />
                            </div>

                            {/* Name */}
                            <div className="form-group">
                                <label htmlFor="name">الاسم</label>
                            <input
                            type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    className="form-input"
                                    placeholder="أدخل اسمك"
                                />
                            </div>

                            {/* Email */}
                            <div className="form-group">
                                <label htmlFor="email">البريد الإلكتروني</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    className="form-input"
                                    placeholder="أدخل بريدك الإلكتروني"
                                />
                            </div>

                            {/* Password */}
                            <div className="form-group">
                                <label htmlFor="password">كلمة المرور</label>
                        <input
                            type="password"
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    required
                                    className="form-input"
                                    placeholder="أدخل كلمة المرور"
                                    minLength="6"
                                />
                            </div>

                            {/* Confirm Password */}
                            <div className="form-group">
                                <label htmlFor="confirmPassword">تأكيد كلمة المرور</label>
                        <input
                            type="password"
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange}
                                    required
                                    className={`form-input ${formData.confirmPassword && formData.password !== formData.confirmPassword ? 'error' : ''}`}
                                    placeholder="أعد إدخال كلمة المرور"
                                    minLength="6"
                                />
                                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                                    <span className="error-text">كلمة المرور غير متطابقة</span>
                                )}
                            </div>

                            {/* Stage Selection (for students only) */}
                            {userType === 'student' && (
                                <div className="form-group">
                                    <label htmlFor="stage_id">المرحلة الدراسية</label>
                                    <select
                                        id="stage_id"
                                        name="stage_id"
                                        value={formData.stage_id}
                                        onChange={handleStageChange}
                                        required
                                        className="form-input"
                                        disabled={stagesLoading}
                                    >
                                        <option value="">اختر المرحلة</option>
                                        {stages.map((stage) => (
                                            <option key={stage.id} value={stage.id}>
                                                {stage.name}
                                            </option>
                                        ))}
                                    </select>
                                    {stagesLoading && <span className="loading-text">جاري تحميل المراحل...</span>}
                                </div>
                            )}

                            {/* Class Selection (for students only) */}
                            {userType === 'student' && formData.stage_id && (
                                <div className="form-group">
                                    <label htmlFor="class_id">الصف الدراسي</label>
                                    <select
                                        id="class_id"
                                        name="class_id"
                                        value={formData.class_id}
                                        onChange={handleInputChange}
                                        required
                                        className="form-input"
                                        disabled={classesLoading}
                                    >
                                        <option value="">اختر الصف</option>
                                        {classes.map((classItem) => (
                                            <option key={classItem.id} value={classItem.id}>
                                                {classItem.name}
                                            </option>
                                        ))}
                                    </select>
                                    {classesLoading && <span className="loading-text">جاري تحميل الفصول...</span>}
                                </div>
                            )}

                            {/* Hidden fields */}
                            <input type="hidden" name="learn_type" value="courses" />
                            <input type="hidden" name="role" value={getRoleValue()} />
                            {/* Hide stage_id and class_id for non-students */}
                            {userType !== 'student' && (
                                <>
                                    <input type="hidden" name="stage_id" value="" />
                                    <input type="hidden" name="class_id" value="" />
                                </>
                            )}

                            {/* Submit Button */}
                            <button 
                                type="submit"
                                className={`login-btn ${isLoading ? 'disabled' : ''}`}
                                disabled={isLoading}
                            >
                                {isLoading ? 'جاري التسجيل...' : 'إكمال التسجيل'}
                            </button>
                        </form>

                        {/* Back Button */}
                        <button 
                            className="back-btn my-3"
                            onClick={() => navigate('/otp')}
                        >
                            ← العودة
                        </button>

                        <a href="/" className="login-back-link">العودة للصفحة الرئيسية</a>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RegisterData;
